import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { stats } from '@/mocks/home';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q8oesnlsngrm4lltgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormStatus('error');
        setFormError(String(msg));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4">
              关于我们
            </h1>
          </div>
        </div>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4">
                  品牌介绍
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  打造中国间谍动作剧新标杆
                </h2>
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    赤裸特工诞生于一个简单的信念：中国观众值得拥有世界级的间谍动作剧集。我们汇聚了国内外顶尖的编剧、导演、演员和制作团队，以电影级的标准打造每一集内容。
                  </p>
                  <p>
                    自首播以来，赤裸特工凭借扣人心弦的剧情、震撼的动作场面和深入人心的角色塑造，迅速成为现象级文化事件。累计超过50亿播放量，豆瓣9.2分的高评价，以及12项行业大奖的肯定，都是观众和业界对我们的认可。
                  </p>
                  <p>
                    我们的使命不仅是提供优质娱乐内容，更是通过特工题材这一独特视角，探讨忠诚、正义、牺牲与人性等永恒主题。赤裸特工的每一个故事，都旨在让观众在享受视听盛宴的同时，获得更深层次的思考与共鸣。
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20film%20production%20studio%20with%20warm%20dramatic%20lighting%2C%20professional%20camera%20equipment%20on%20set%2C%20creative%20filmmaking%20atmosphere%2C%20behind%20the%20scenes%20aesthetic%2C%20rich%20amber%20and%20golden%20tones%2C%20artistic%20composition%2C%20movie%20production%20environment&width=700&height=500&seq=about-brand&orientation=landscape"
                  alt="赤裸特工品牌 - 影视制作"
                  title="赤裸特工品牌介绍 - 影视制作幕后"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium text-accent-700 bg-accent-100 rounded-full mb-4">
                口碑声誉
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                用数据说话
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                赤裸特工的口碑与成就，来自每一位观众的支持与认可
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-lg p-6 text-center border border-background-200"
                >
                  <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  quote: '赤裸特工重新定义了中国间谍剧的类型边界，其叙事野心和制作水准令人惊叹。',
                  author: '《影视评论》杂志',
                  role: '年度最佳剧集',
                },
                {
                  quote: '张凯的表演让人忘记了他在演戏，他让观众相信，他就是那个在黑暗中负重前行的特工。',
                  author: '李墨',
                  role: '著名影评人',
                },
                {
                  quote: '每一集都像是一部精心制作的电影，赤裸特工为电视剧行业树立了新的品质标杆。',
                  author: '金鹰奖评委会',
                  role: '最佳剧集奖',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-6 border border-background-200"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-400 mb-3 block"></i>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4 italic">
                    "{item.quote}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.author}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50" id="contact">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium text-secondary-700 bg-secondary-100 rounded-full mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                与我们取得联系
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto leading-relaxed">
                如有任何问题、合作意向或媒体咨询，请填写以下表单，我们将尽快回复。
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200"
                  placeholder="请输入主题"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <div className="mt-4 p-3 rounded-md bg-accent-100 text-accent-800 text-sm font-medium">
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}

              {formStatus === 'error' && formError && (
                <div className="mt-4 p-3 rounded-md bg-primary-100 text-primary-800 text-sm font-medium">
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于赤裸特工',
            description: '赤裸特工官方品牌介绍，了解我们的创作理念、口碑声誉与联系方式。',
            url: 'https://www.chiluotegong.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '赤裸特工官方',
              url: 'https://www.chiluotegong.com/',
            },
            about: {
              '@type': 'Organization',
              name: '赤裸特工官方',
              description: '赤裸特工是中国领先的间谍动作剧品牌，致力于打造世界级的影视内容。',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.chiluotegong.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}